import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'react-toastify';
import {
  MAX_SPACE_FILE_UPLOAD_BATCH,
  SpaceErrorCode,
  SpaceFileUploadsView,
  SpaceFileUploadView,
  spaceMimeTypeFromName,
} from 'shared';

import { QueryKey } from '../../../constants/index';
import { HttpException } from '../../../exceptions/index';
import { useAppMutation, useLocale } from '../../../hooks/index';
import { ApiService } from '../../../services/index';
import { selectedCompanyStorage } from '../../../storage/company/selected-company.util';
import { FILE_INVALIDATION_KEYS, SPACE_BACKEND_ROUTE } from '../constants/spaces-query.const';
import { TreeSpaceRef } from '../types/data-room.types';
import { chunksOf } from '../utils/chunks.util';
import { uploadFileSchema } from '../validations/upload-file.validation';

type UploadTarget = {
  folderId: null | string;
  space: TreeSpaceRef;
};

type UploadVariables = { files: File[] } & UploadTarget;

type FileDeclaration = { contentType: string; name: string; size: number };

// Where the files on their way are going and how many of the batch in flight are not listed yet;
// the browser shows that many placeholder rows in that folder's table alone.
export type SpaceUploadInFlight = { count: number; folderId: null | string; spaceId: string };

// A few puts at a time: each is up to 10 MB straight to storage, then one call to record it.
const PARALLEL_UPLOADS = 3;

// The server's unique index ignores case and surrounding spaces, so names are compared the same way.
const nameKey = (name: string) => name.trim().toLowerCase();

const toDeclaration = (file: File): FileDeclaration => ({
  contentType: file.type,
  name: file.name,
  size: file.size,
});

// A file the browser gave no type for (an extension its system does not know) takes the type its
// name implies, so it is judged, declared and put under one type rather than refused for none.
const withKnownType = (file: File): File =>
  file.type
    ? file
    : new File([file], file.name, {
        lastModified: file.lastModified,
        type: spaceMimeTypeFromName(file.name),
      });

const isNameTaken = (error: unknown): boolean =>
  error instanceof HttpException && error.body?.code === SpaceErrorCode.FILE_NAME_TAKEN;

// One request signs a whole batch of one folder's files; the server checks each name once.
const requestUploads = (files: File[], { folderId, space }: UploadTarget) =>
  ApiService.post<SpaceFileUploadsView, { files: FileDeclaration[]; folderId: null | string }>(
    { endpoint: SPACE_BACKEND_ROUTE[space.kind], routePath: [space.id, 'file-uploads'] },
    { body: { files: files.map(toDeclaration), folderId } },
  );

// The browser puts the bytes straight to storage, then the server records the file.
const putAndRegister = async (
  file: File,
  { fileId, uploadUrl }: SpaceFileUploadView,
  { folderId, space }: UploadTarget,
): Promise<void> => {
  await ApiService.uploadFileToPresignedUrl().callback(uploadUrl, file);

  await ApiService.post<unknown, { fileId: string; folderId: null | string } & FileDeclaration>(
    { endpoint: SPACE_BACKEND_ROUTE[space.kind], routePath: [space.id, 'files'] },
    { body: { ...toDeclaration(file), fileId, folderId } },
  );
};

export const useUploadSpaceFiles = () => {
  const companyId = selectedCompanyStorage(({ companyId: selected }) => selected);
  const { messagesLocale, messagesLocaleFn } = useLocale();
  const queryClient = useQueryClient();
  const [inFlight, setInFlight] = useState<null | SpaceUploadInFlight>(null);

  // Awaited, so a batch's placeholders give way only once its rows are in the table.
  const refreshFolder = () =>
    Promise.all(
      FILE_INVALIDATION_KEYS.map((key) => queryClient.invalidateQueries({ queryKey: [key] })),
    );

  // One toast for a whole drop: its only problem as it is, several as the first and a count, so a
  // folder of bad files does not stack a toast each.
  const reportProblems = (problems: string[]) => {
    if (!problems.length) return;

    toast.error(
      problems.length === 1
        ? problems[0]
        : messagesLocaleFn('FILE_UPLOAD_MORE_ISSUES', {
            count: problems.length - 1,
            message: problems[0],
          }),
    );
  };

  const rejection = (file: File, reason: string) =>
    messagesLocaleFn('FILE_UPLOAD_REJECTED', { name: file.name, reason });

  const failure = (file: File) => messagesLocaleFn('FILE_UPLOAD_FAILED', { name: file.name });

  // The server's reason refuses the whole batch at once — the folder is gone, the room inactive —
  // so it is one problem; without one, each file failed.
  const batchProblems = (batch: File[], error: unknown): string[] => {
    const reason = error instanceof HttpException ? error.body?.message : undefined;

    if (!reason) return batch.map(failure);

    return [Array.isArray(reason) ? reason.join(' ') : reason];
  };

  // The rules the server applies, checked before anything is sent; a name twice in one drop keeps
  // its first file only, as the folder could never hold both.
  const acceptable = (files: File[], problems: string[]): File[] => {
    const seen = new Set<string>();

    return files.map(withKnownType).filter((file) => {
      const check = uploadFileSchema.safeParse({
        name: file.name,
        size: file.size,
        type: file.type,
      });

      if (!check.success) {
        problems.push(rejection(file, check.error.issues[0].message));

        return false;
      }

      if (seen.has(nameKey(file.name))) {
        problems.push(rejection(file, messagesLocale['FILE_NAME_ALREADY_EXISTS']));

        return false;
      }

      seen.add(nameKey(file.name));

      return true;
    });
  };

  const { isPending, mutate: uploadFiles } = useAppMutation<number, HttpException, UploadVariables>(
    [QueryKey.UPLOAD_SPACE_FILES, companyId],
    {
      // Each file reports its own outcome, so one bad file never hides the others.
      defaultErrorHandling: false,
      mutationFn: async ({ files, ...target }) => {
        const problems: string[] = [];
        const accepted = acceptable(files, problems);

        if (!accepted.length) {
          reportProblems(problems);

          return 0;
        }

        const { folderId, space } = target;
        const showInFlight = (count: number) => setInFlight({ count, folderId, spaceId: space.id });
        let uploaded = 0;

        try {
          for (const batch of chunksOf(accepted, MAX_SPACE_FILE_UPLOAD_BATCH)) {
            const byName = new Map(batch.map((file) => [nameKey(file.name), file]));
            let tickets: SpaceFileUploadsView;

            showInFlight(batch.length);

            try {
              tickets = await requestUploads(batch, target);
            } catch (error) {
              problems.push(...batchProblems(batch, error));
              continue;
            }

            tickets.rejected.forEach(({ name }) => {
              const file = byName.get(nameKey(name));

              if (file) problems.push(rejection(file, messagesLocale['FILE_NAME_ALREADY_EXISTS']));
            });
            showInFlight(tickets.uploads.length);

            let batchUploaded = 0;

            for (const group of chunksOf(tickets.uploads, PARALLEL_UPLOADS)) {
              const results = await Promise.allSettled(
                group.map((ticket) => {
                  const file = byName.get(nameKey(ticket.name));

                  return file
                    ? putAndRegister(file, ticket, target)
                    : Promise.reject(new Error(`No file for ${ticket.name}`));
                }),
              );

              results.forEach((result, index) => {
                const file = byName.get(nameKey(group[index].name));

                if (result.status === 'fulfilled') {
                  batchUploaded += 1;
                } else if (file && isNameTaken(result.reason)) {
                  problems.push(rejection(file, messagesLocale['FILE_NAME_ALREADY_EXISTS']));
                } else if (file) {
                  problems.push(failure(file));
                }
              });
            }

            if (batchUploaded) await refreshFolder();

            uploaded += batchUploaded;
          }
        } finally {
          setInFlight(null);
          reportProblems(problems);
        }

        return uploaded;
      },
      onSuccess: (uploaded) => {
        if (uploaded) toast.success(messagesLocale['FILES_UPLOADED']);
      },
    },
  );

  return { inFlight, isUploading: isPending, uploadFiles };
};
